// Check pages workspace model (05 §6.2). The snapshot is 01's IngestionView
// (GET /ingestion/{jobId}); normalise() indexes it by id. Page and script
// objects keep 01's field names (pageId, scriptId, railKind, …) so a server
// view from a command reply can replace them one for one.
//
// Workspace = {
//   jobId, ownerKind, mode, status, annotated, version, undoDepth,
//   counts, summary, bulkAccept, credit, questions[], files[], roster[],
//   rosterOnly (BULK_UPLOAD on a class task: only roster members commit),
//   pages: {[pageId]: PageView}, scripts: {[scriptId]: ScriptView},
//   scriptOrder: [scriptId] (by scriptNo), looseIds: [pageId] (RESPONSE, no
//   script, by order), setAsideIds: [pageId] (kind !== RESPONSE, by order),
// }

export const RESPONSE = 'RESPONSE';

const byOrder = (pages) => (a, b) =>
  (pages[a]?.order ?? 0) - (pages[b]?.order ?? 0);

/** Recompute the derived id lists after any change. Returns a new object. */
export const finalize = (ws) => {
  const pages = ws.pages || {};
  const scripts = ws.scripts || {};
  const pageIds = Object.keys(pages);
  const looseIds = pageIds
    .filter((id) => pages[id].kind === RESPONSE && !pages[id].scriptId)
    .sort(byOrder(pages));
  const setAsideIds = pageIds
    .filter((id) => pages[id].kind !== RESPONSE)
    .sort(byOrder(pages));
  const scriptOrder = Object.keys(scripts).sort(
    (a, b) =>
      (scripts[a].scriptNo ?? 0) - (scripts[b].scriptNo ?? 0) ||
      String(a).localeCompare(String(b))
  );
  return { ...ws, looseIds, setAsideIds, scriptOrder };
};

const indexBy = (list, key) => {
  const out = {};
  (Array.isArray(list) ? list : []).forEach((entry) => {
    if (entry && entry[key]) out[entry[key]] = entry;
  });
  return out;
};

const normaliseScript = (script) => ({
  ...script,
  pageIds: Array.isArray(script.pageIds) ? [...script.pageIds] : [],
  questionStarts: Array.isArray(script.questionStarts)
    ? script.questionStarts
    : [],
});

/** IngestionView → Workspace. */
export const normalise = (view) => {
  const scripts = {};
  (Array.isArray(view?.scripts) ? view.scripts : []).forEach((script) => {
    if (script?.scriptId) scripts[script.scriptId] = normaliseScript(script);
  });
  return finalize({
    jobId: view?.jobId ?? null,
    ownerKind: view?.ownerKind ?? null,
    ownerId: view?.ownerId ?? null,
    mode: view?.mode ?? 'AUTO',
    status: view?.status ?? null,
    annotated: view?.annotated ?? null,
    version: view?.version ?? 0,
    undoDepth: view?.undoDepth ?? 0,
    counts: view?.counts ?? {},
    summary: view?.summary ?? null,
    bulkAccept: view?.bulkAccept ?? null,
    credit: view?.credit ?? null,
    questions: Array.isArray(view?.questions) ? view.questions : [],
    files: Array.isArray(view?.files) ? view.files : [],
    roster: Array.isArray(view?.roster) ? view.roster : [],
    // 06 D11: a class task's Bulk Upload routes only roster members.
    rosterOnly: Boolean(view?.rosterOnly),
    pages: indexBy(view?.pages, 'pageId'),
    scripts,
  });
};

/**
 * Merge a command / undo reply (01 §6.1) into the confirmed workspace:
 * changed scripts and pages replace theirs, removedScriptIds disappear, and
 * version, undoDepth, summary, bulkAccept and credit follow the reply.
 */
export const mergeReply = (ws, reply) => {
  if (!ws || !reply) return ws;
  const pages = { ...ws.pages };
  const scripts = { ...ws.scripts };
  const changed = reply.changed || {};
  (changed.pages || []).forEach((page) => {
    if (page?.pageId) pages[page.pageId] = page;
  });
  (changed.scripts || []).forEach((script) => {
    if (script?.scriptId) scripts[script.scriptId] = normaliseScript(script);
  });
  (changed.removedScriptIds || []).forEach((id) => {
    delete scripts[id];
  });
  return finalize({
    ...ws,
    pages,
    scripts,
    version: reply.version ?? ws.version,
    undoDepth: reply.undoDepth ?? ws.undoDepth,
    summary: reply.summary ?? ws.summary,
    bulkAccept: reply.bulkAccept ?? ws.bulkAccept,
    credit: reply.credit === undefined ? ws.credit : reply.credit,
    mode: reply.mode ?? ws.mode,
  });
};

/** Label of a script for menus and toasts: the name, else "Script 07". */
export const scriptName = (script) =>
  (script?.name && String(script.name).trim()) ||
  (script?.studentId && String(script.studentId).trim()) ||
  script?.label ||
  (script?.scriptNo
    ? `Script ${String(script.scriptNo).padStart(2, '0')}`
    : 'Script');

/** "Script 07" for a script number. */
export const scriptLabelFor = (no) => `Script ${String(no).padStart(2, '0')}`;

/** The roster entry by id, or null. */
export const rosterEntry = (ws, rosterId) =>
  (ws?.roster || []).find((entry) => entry.rosterId === rosterId) || null;

/** Attach roster entries (GET /ingestion/{jobId}/roster) to a workspace. */
export const withRoster = (ws, entries) =>
  ws && Array.isArray(entries) && entries !== ws.roster
    ? { ...ws, roster: entries }
    : ws;
